import _ from 'lodash'
import * as THREE from 'three'

import Config from '@glass-designer/config'
import { EditorUserError } from './../utils/editorErrors'
import MathsUtils from '@/utils/mathsUtils'
import EditorUtils from './../utils/editorUtils'
import BaseObject from '../objects/baseObject'
import LineObject from '../library/three/LineObject'
import DashedLineObject from '@glass-designer/objects/dashedLineObject'

import GlassDesignerTypes from '../types'


/*
 * GlassDatumLine
 * A floating datum line for a shape, created by measurement tool
 * Measurements, datum links, dimensions can be referenced on datum lines
 */
export default class GlassDatumLine extends BaseObject {
  constructor(editor, lineData = {}) {
    super(editor)
    this.objectClassName = 'GlassDatumLine'

    // Rendering details
    this.sceneObject = new THREE.Object3D()
    this.editor.scene.add(this.sceneObject)

    this.dashedLineObject = new DashedLineObject(this.editor, {
      colour: this.editor.getTheme().datumLineColour,
      dashScale: 1.5,
      dashSize: 12,
      gapSize: 8
    })
    this.dashedLineObject.setVisible(true)
    this.addChildObject(this.dashedLineObject)

    this.selectedLine = null

    this.startPosition = new THREE.Vector2(0, 0)
    this.endPosition = new THREE.Vector2(0, 0)

    if(lineData.startPosition)
      this.startPosition.set(lineData.startPosition.x, lineData.startPosition.y)
    if(lineData.endPosition)
      this.endPosition.set(lineData.endPosition.x, lineData.endPosition.y)

    this.listenMouseEvents = true

    this.isDimensionDisplayed = !!lineData.isDimensionDisplayed

    this.updateSceneObject()
  }


  /**
   * Dispose the object
   */
  dispose() {
    super.dispose()

    // Remove stuff
    if(this.dashedLineObject) {
      this.dashedLineObject.dispose()
      this.dashedLineObject = null
    }

    if(this.selectedLine) {
      this.selectedLine.dispose()
      this.selectedLine = null
    }
  }


  /**
   * onBeforeRender
   * Called prior to rendering of the object
   */
  onBeforeRender() {
    super.onBeforeRender()

    // Selected support
    if(this.selectedLine) {
      this.selectedLine.updateResolution(this.editor.settings.viewportWidth, this.editor.settings.viewportHeight)
      this.selectedLine.setVisible(this.selected)
    }
  }


  /**
   * Mouse Move Event
   */
  onMouseMoveEvent(ev, mouseData, intersectionData = {}) {
    if(this.editor.isSelectEnabled())
      this.editor.setMouseCursorHand()
  }


  /**
   * Update the view level on the object
   * Adjusts the scene object matrix & visibility for editing / viewing modes
   */
  viewLevelUpdated(viewLevel) {
    // Hide the datum lines for certain view modes
    let visible = (viewLevel == GlassDesignerTypes.EditorViewLevelTypes.normal || viewLevel == GlassDesignerTypes.EditorViewLevelTypes.technical)

    if(this.lineObject)
      this.lineObject.visible = visible
  }


  /**
   * Get the length of this datum line
   */
  getLength() {
    return this.startPosition.distanceTo(this.endPosition)
  }


  /**
   * Calculate the direction of the datum line
   * Returns a normalized Vector2 object
   */
  getDirection() {
    var dir = new THREE.Vector2()
    dir.subVectors(this.endPosition, this.startPosition)
    dir.normalize()
    return dir
  }


  /**
   * Calculate the outwards direction of the datum line
   * Returns a normalized Vector2 object
   */
  getOutDirection() {
    let dir = this.getDirection()

    // Rotate by 90deg in 2D
    var outDir = new THREE.Vector2(dir.y, -dir.x)
    return outDir
  }
  

  /**
   * Calculate the angle of the 2D segment direction in radians
   * Start -> Endpoint is used for both lines & arcs
   * Arc direction is just an approximation & should not really use this function
   */
  getAngle() {
    var dir = this.getDirection()
    return dir.angle()
  }


  /**
   * Calculates the midpoint of the datum line
   */
  getMidpoint() {
    return new THREE.Vector2((this.endPosition.x + this.startPosition.x) / 2, (this.endPosition.y + this.startPosition.y) / 2)
  }


  /**
   * Project a point onto the datum line & return the closest point on the datum line to the given point
   * Returns a Vector2 object with the closest point
   */
  projectPointOnDatumLine(point, clampEnds = true) {
    return MathsUtils.getClosest2DPointOnLineSegment(this.startPosition, this.endPosition, new THREE.Vector2(point.x, point.y), clampEnds)
  }


  /**
   * Update the scene object
   */
  updateSceneObject() {
    if(this.selectedLine) {
      this.selectedLine.dispose()
      this.selectedLine = null
    }
    this.clearSceneObject()

    this.selectedLine = new LineObject({
      colour: this.editor.getTheme().selectionColour,
      width: this.editor.getTheme().selectionWidth
    })
    this.selectedLine.updateLinePath([this.startPosition, this.endPosition])
    this.sceneObject.add(this.selectedLine.lineObj)

    this.dashedLineObject.setStartPosition(this.startPosition)
    this.dashedLineObject.setEndPosition(this.endPosition)
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    // Ignore if not visible
    if(!this.isVisible())
      return null

    var closestIntersection = null
    let radiusTest = Math.max(worldRadius, 0.1)
    let lineRadiusTest = radiusTest * 1.5

    // Create a plane from the glass shape
    // Assumes the 2D shape is sitting on the plane at origin (Z = 0)
    var shapePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(shapePlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)

    let intPoint = this.projectPointOnDatumLine(ray2DPoint)
    let dist = ray2DPoint.distanceTo(intPoint)
    
    if(dist <= lineRadiusTest) {
      closestIntersection = {
        intersection: intPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist
      }
    }

    return closestIntersection
  }


  /**
   * Calculate the intersection between a line (given a start & end point) and this datum line
   * Returns a Vector2 object for the intersection point
   * Returns null if no intersection occured
   */
  calculateLineIntersection(startPoint, endPoint) {
    var intersectionPoint = new THREE.Vector2()
    if(MathsUtils.checkSegmentSegmentIntersection(startPoint, endPoint, this.startPosition, this.endPosition, intersectionPoint)) {
      return intersectionPoint
    }

    return null
  }

  
  /**
   * Clone the GlassDatumLine
   */
  clone() {
    var newDatumLine = new GlassDatumLine(this.editor, {
      startPosition: this.startPosition,
      endPosition: this.endPosition,
      isDimensionDisplayed: this.isDimensionDisplayed
    })

    return newDatumLine
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {    
    var outputData = super.serialize()

    outputData.startPosition = MathsUtils.serializeVector2(this.startPosition)
    outputData.endPosition = MathsUtils.serializeVector2(this.endPosition)
    outputData.isDimensionDisplayed = this.isDimensionDisplayed

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.startPosition = MathsUtils.deserializeVector2(object.startPosition)
    this.endPosition = MathsUtils.deserializeVector2(object.endPosition)
    this.isDimensionDisplayed = !!object.isDimensionDisplayed

    this.updateSceneObject()
  }

}